/**
 * InstancesPanel — správa ACM-provisioned GitLab instancí.
 *
 * Widget-first pattern (wiki `acm-ui-widget-architecture`): tato komponenta je
 * `component:` custom widgetu `gitlab.instances` (viz `widgets/registry.gitlab.tsx`).
 * WidgetShell obalí Card + hlavičkou (title, icon, status, node picker) —
 * komponenta uvnitř vykresluje JEN obsah (žádný vnější Card ani CardHeader).
 *
 * Full lifecycle přes MCP tooly:
 *   - `gitlab_instances_list` — přehled instancí (name, status, ports, external_url)
 *   - `gitlab_provision` — nová instance (kontejner acm-gitlab-<name>)
 *   - `gitlab_bootstrap` — dokončit provisioning (root PAT + connection register)
 *   - `gitlab_manage` — start/stop/restart přes docker
 *   - `gitlab_delete` — canonical destroy (PIN gate + blocking backup + sweep)
 *   - `gitlab_backup` / `gitlab_upgrade` — údržba
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Archive,
  ArrowUpCircle,
  Settings,
  ExternalLink,
  Play,
  Plus,
  Rocket,
  RotateCcw,
  Server,
  Square,
  Trash2,
  Upload,
} from 'lucide-react';
import { api } from '../../../lib/api';
import {
  Badge,
  Button,
  EmptyState,
  Input,
  Modal,
  StatusDot,
} from '../../../components/ui';
import type { WidgetRenderProps } from '../../../widgets/types';

interface Instance {
  name: string;
  container: string;
  version: string;
  status: string;
  http_port: number | null;
  ssh_port: number | null;
  external_url: string | null;
  tenant_id: string | null;
  node: string | null;
  connection_id?: string | null;
  bootstrapped?: boolean;
}

interface InstancesResp {
  ok: boolean;
  count?: number;
  instances: Instance[];
}

/** Verdikt gitlab.versions_available pro jednu instanci. */
interface VersionVerdict {
  name: string;
  current_version: string | null;
  latest_version: string | null;
  latest_patch: string | null;
  upgrade_available: boolean;
  /** critical = GitLab sám hlásí kritickou opravu; available = novější bez naléhavosti. */
  severity?: 'critical' | 'available' | 'current';
  severity_source?: 'gitlab' | 'none';
  /** = severity === 'critical' */
  asap: boolean;
  recommended: string | null;
  behind_by: number;
  newer_versions?: string[];
}

interface VersionsResp {
  ok: boolean;
  source?: 'registry' | 'unavailable';
  latest?: string | null;
  versions?: string[];
  instances?: VersionVerdict[];
  note?: string;
}

function statusToDot(status: string): 'running' | 'stopped' | 'error' | 'pending' {
  if (status === 'running') return 'running';
  if (status === 'error') return 'error';
  if (status === 'deleting' || status === 'provisioning' || status === 'created') return 'pending';
  return 'stopped';
}

export function InstancesPanel(_: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [newOpen, setNewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Instance | null>(null);
  const [bootstrapTarget, setBootstrapTarget] = useState<Instance | null>(null);
  const [upgradeTarget, setUpgradeTarget] = useState<Instance | null>(null);
  const [settingsTarget, setSettingsTarget] = useState<Instance | null>(null);

  const q = useQuery<InstancesResp>({
    queryKey: ['gitlab-instances'],
    queryFn: () => api.mcp<InstancesResp>('gitlab.instances_list', {}, { silent: true }),
    staleTime: 15_000,
  });

  const instances = q.data?.instances ?? [];

  // Verze se tahají zvlášť — registry je vzdálený a nemá blokovat výpis instancí.
  const vq = useQuery<VersionsResp>({
    queryKey: ['gitlab-versions'],
    queryFn: () => api.mcp<VersionsResp>('gitlab.versions_available', {}, { silent: true }),
    staleTime: 10 * 60_000,
  });
  const verdictFor = (name: string): VersionVerdict | undefined =>
    vq.data?.instances?.find((v) => v.name === name);

  const manageMut = useMutation({
    mutationFn: (args: { name: string; action: 'start' | 'stop' | 'restart' }) =>
      api.mcp<{ ok?: boolean; error?: string }>('gitlab.manage', args, {
        label: t('gitlab.instances.actionLabel', { action: args.action, name: args.name }),
      }),
    onSuccess: (r) => {
      if (r?.ok === false) return; // wrapper už zobrazil error toast
      qc.invalidateQueries({ queryKey: ['gitlab-instances'] });
    },
  });

  const backupMut = useMutation({
    mutationFn: (name: string) =>
      api.mcp<{ ok?: boolean; error?: string }>('gitlab.backup', { name }, {
        label: t('gitlab.instances.backupLabel', { name }),
      }),
    onSuccess: (r) => {
      if (r?.ok === false) return;
      toast.success(t('gitlab.instances.backupOk'));
    },
  });

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-400">
          {t('gitlab.instances.title', { count: instances.length })}
        </span>
        <Button variant="primary" size="sm" onClick={() => setNewOpen(true)}>
          <Plus size={13} /> {t('gitlab.instances.newBtn')}
        </Button>
      </div>

      {q.isLoading
        ? <div className="text-sm text-zinc-500">{t('common.loading')}</div>
        : instances.length === 0
        ? (
          <EmptyState
            title={t('gitlab.instances.emptyTitle')}
            description={t('gitlab.instances.emptyDesc')}
          />
        )
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-zinc-800 text-xs text-zinc-400">
                  <th className="text-left py-2 pr-3">{t('gitlab.instances.colName')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.instances.colNode', 'Node')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.instances.colStatus')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.instances.colVersion')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.instances.colUrl')}</th>
                  <th className="text-left py-2 pr-3">{t('gitlab.instances.colPorts')}</th>
                  <th className="text-right py-2 pr-3">{t('gitlab.instances.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {instances.map((inst) => (
                  <InstanceRow
                    key={inst.name}
                    inst={inst}
                    onManage={(action) => manageMut.mutate({ name: inst.name, action })}
                    onBackup={() => backupMut.mutate(inst.name)}
                    onDelete={() => setDeleteTarget(inst)}
                    onBootstrap={() => setBootstrapTarget(inst)}
                    onUpgrade={() => setUpgradeTarget(inst)}
                    onSettings={() => setSettingsTarget(inst)}
                    verdict={verdictFor(inst.name)}
                    pending={manageMut.isPending || backupMut.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

      {newOpen && (
        <NewInstanceModal
          existing={instances}
          onClose={() => setNewOpen(false)}
          onCreated={() => {
            setNewOpen(false);
            qc.invalidateQueries({ queryKey: ['gitlab-instances'] });
          }}
        />
      )}

      {deleteTarget && (
        <DeleteInstanceModal
          inst={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            qc.invalidateQueries({ queryKey: ['gitlab-instances'] });
          }}
        />
      )}

      {settingsTarget && (
        <SettingsModal
          inst={settingsTarget}
          onClose={() => setSettingsTarget(null)}
        />
      )}

      {upgradeTarget && (
        <UpgradeModal
          inst={upgradeTarget}
          verdict={verdictFor(upgradeTarget.name)}
          versions={vq.data?.versions ?? []}
          versionsUnavailable={vq.data?.source === 'unavailable'}
          onClose={() => setUpgradeTarget(null)}
          onDone={() => {
            setUpgradeTarget(null);
            qc.invalidateQueries({ queryKey: ['gitlab-instances'] });
          }}
        />
      )}

      {bootstrapTarget && (
        <BootstrapModal
          inst={bootstrapTarget}
          onClose={() => setBootstrapTarget(null)}
          onDone={() => {
            setBootstrapTarget(null);
            qc.invalidateQueries({ queryKey: ['gitlab-instances'] });
            qc.invalidateQueries({ queryKey: ['gitlab-connections'] });
          }}
        />
      )}
    </div>
  );
}

// ── Instance row ────────────────────────────────────────────────────────────

function InstanceRow(
  { inst, onManage, onBackup, onDelete, onBootstrap, onUpgrade, onSettings, verdict, pending }: {
  inst: Instance;
  onManage: (action: 'start' | 'stop' | 'restart') => void;
  onBackup: () => void;
  onDelete: () => void;
  onBootstrap: () => void;
  onUpgrade: () => void;
  onSettings: () => void;
  verdict?: VersionVerdict;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const running = inst.status === 'running';
  const isCreated = inst.status === 'created'; // provision bez start:true nebo neúspěšný
  return (
    <tr className="border-b border-zinc-900 hover:bg-zinc-900/30">
      <td className="py-2 pr-3">
        <div className="font-medium flex items-center gap-2">
          <StatusDot status={statusToDot(inst.status)} />
          {inst.name}
        </div>
        <div className="text-xs text-zinc-500">{inst.container}</div>
      </td>
      <td className="py-2 pr-3">
        {inst.node
          ? (
            <span className="inline-flex items-center gap-1 text-xs text-zinc-300">
              <Server size={11} className="text-zinc-500 shrink-0" />
              <span className="font-mono">{inst.node}</span>
            </span>
          )
          : <span className="text-xs text-zinc-600">—</span>}
      </td>
      <td className="py-2 pr-3">
        <Badge variant={running ? 'green' : inst.status === 'error' ? 'red' : 'yellow'}>
          {inst.status}
        </Badge>
      </td>
      <td className="py-2 pr-3">
        <div className="text-xs font-mono text-zinc-300">{inst.version || '—'}</div>
        {verdict?.upgrade_available && (
          <div className="mt-0.5 flex items-center gap-1">
            <span className="text-[11px] font-mono text-zinc-400">
              → {verdict.recommended}
            </span>
            {verdict.asap
              ? <Badge variant="red">{t('gitlab.instances.versionAsap')}</Badge>
              : <Badge variant="yellow">{t('gitlab.instances.versionNewer')}</Badge>}
          </div>
        )}
        {verdict && !verdict.upgrade_available && verdict.latest_version && (
          <div className="text-[11px] text-zinc-600">{t('gitlab.instances.versionCurrent')}</div>
        )}
      </td>
      <td className="py-2 pr-3">
        {inst.external_url
          ? (
            <a
              href={inst.external_url}
              target="_blank"
              rel="noreferrer"
              className="text-sky-400 hover:text-sky-300 inline-flex items-center gap-1 text-xs"
            >
              {inst.external_url.replace(/^https?:\/\//, '')} <ExternalLink size={11} />
            </a>
          )
          : <span className="text-xs text-zinc-600">—</span>}
      </td>
      <td className="py-2 pr-3 text-xs text-zinc-400 font-mono">
        {inst.http_port ? `http:${inst.http_port}` : '—'}
        {inst.ssh_port && <span className="ml-2">ssh:{inst.ssh_port}</span>}
      </td>
      <td className="py-2 pr-3">
        <div className="flex items-center justify-end gap-1">
          {isCreated && (
            <Button
              variant="secondary"
              size="sm"
              disabled={pending}
              onClick={onBootstrap}
              title={t('gitlab.instances.bootstrap')}
            >
              <Rocket size={13} />
            </Button>
          )}
          {!isCreated && (
            <>
              <Button
                variant="ghost"
                size="sm"
                disabled={pending || running}
                onClick={() => onManage('start')}
                title={t('gitlab.instances.start')}
              >
                <Play size={13} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={pending || !running}
                onClick={() => onManage('stop')}
                title={t('gitlab.instances.stop')}
              >
                <Square size={13} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={pending}
                onClick={() => onManage('restart')}
                title={t('gitlab.instances.restart')}
              >
                <RotateCcw size={13} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={pending}
                onClick={onBackup}
                title={t('gitlab.instances.backup')}
              >
                <Archive size={13} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={pending}
                onClick={onSettings}
                title={t('gitlab.settings.btn')}
              >
                <Settings size={13} />
              </Button>
              <Button
                variant={verdict?.asap
                  ? 'danger'
                  : verdict?.upgrade_available
                  ? 'primary'
                  : 'ghost'}
                size="sm"
                disabled={pending}
                onClick={onUpgrade}
                title={verdict?.upgrade_available
                  ? t('gitlab.instances.upgradeAvailableTitle', {
                    version: verdict.recommended ?? '',
                  })
                  : t('gitlab.instances.upgrade')}
              >
                <ArrowUpCircle size={13} />
              </Button>
              {!inst.bootstrapped && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pending}
                  onClick={onBootstrap}
                  title={t('gitlab.instances.bootstrap')}
                >
                  <Rocket size={13} />
                </Button>
              )}
            </>
          )}
          <Button
            variant="danger"
            size="sm"
            disabled={pending}
            onClick={onDelete}
            title={t('gitlab.instances.delete')}
          >
            <Trash2 size={13} />
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ── New instance modal ──────────────────────────────────────────────────────

function NewInstanceModal({ existing, onClose, onCreated }: {
  existing: Instance[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  // Suggestion: max used http_port + 1, ssh_port stejně
  const maxHttp = useMemo(
    () => existing.reduce((m, i) => Math.max(m, i.http_port ?? 0), 40100),
    [existing],
  );
  const maxSsh = useMemo(
    () => existing.reduce((m, i) => Math.max(m, i.ssh_port ?? 0), 40200),
    [existing],
  );

  const [name, setName] = useState('');
  const [httpPort, setHttpPort] = useState(String(maxHttp + 1));
  const [sshPort, setSshPort] = useState(String(maxSsh + 1));
  const [memoryLimit, setMemoryLimit] = useState('');
  const [start, setStart] = useState(true);

  const mut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok?: boolean; error?: string; name?: string; root_password?: string }>(
        'gitlab.provision',
        {
          name,
          http_port: Number(httpPort),
          ssh_port: Number(sshPort),
          start,
          ...(memoryLimit ? { memory_limit: memoryLimit } : {}),
        },
        { label: t('gitlab.instances.provisionLabel', { name }) },
      ),
    onSuccess: (r) => {
      if (r?.ok === false) return; // wrapper už řešil error toast
      const pwd = r?.root_password ?? '';
      if (pwd) {
        toast.success(t('gitlab.instances.createdWithPwd', { name: r?.name ?? name, pwd }), {
          duration: 15_000,
        });
      } else {
        toast.success(t('gitlab.instances.created', { name: r?.name ?? name }));
      }
      onCreated();
    },
  });

  const valid = name.trim().length > 0 && Number(httpPort) > 0 && Number(sshPort) > 0;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('gitlab.instances.newTitle')}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={mut.isPending}
            disabled={!valid}
            onClick={() => mut.mutate()}
          >
            {t('gitlab.instances.provisionBtn')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="text-xs text-zinc-400 block mb-1">
            {t('gitlab.instances.fieldName')}
          </label>
          <Input
            placeholder="my-gitlab"
            value={name}
            onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
          />
          <p className="text-[10px] text-zinc-500 mt-0.5">
            {t('gitlab.instances.fieldNameHelp')}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-zinc-400 block mb-1">
              {t('gitlab.instances.fieldHttpPort')}
            </label>
            <Input
              type="number"
              value={httpPort}
              onChange={(e) => setHttpPort(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">
              {t('gitlab.instances.fieldSshPort')}
            </label>
            <Input
              type="number"
              value={sshPort}
              onChange={(e) => setSshPort(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-zinc-400 block mb-1">
            {t('gitlab.instances.fieldMemory')}
          </label>
          <Input
            placeholder="6g"
            value={memoryLimit}
            onChange={(e) => setMemoryLimit(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={start}
            onChange={(e) => setStart(e.target.checked)}
            className="accent-sky-500"
          />
          {t('gitlab.instances.startImmediately')}
        </label>
        <p className="text-[11px] text-zinc-500">
          {t('gitlab.instances.newHelp')}
        </p>
      </div>
    </Modal>
  );
}

// ── Delete instance modal (canonical: PIN + backup + sweep) ─────────────────

interface DeleteResp {
  ok?: boolean;
  name?: string;
  locked?: boolean;
  backup_failed?: boolean;
  was_locked?: boolean;
  backed_up?: boolean;
  error?: string;
  errors?: string[];
}

function DeleteInstanceModal({ inst, onClose, onDeleted }: {
  inst: Instance;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [forceNoBackup, setForceNoBackup] = useState(false);
  const [removeVolumes, setRemoveVolumes] = useState(true);
  const [forceOrphanRunners, setForceOrphanRunners] = useState(false);
  const [showedLocked, setShowedLocked] = useState(false);

  const mut = useMutation<DeleteResp>({
    mutationFn: () =>
      api.mcp<DeleteResp>(
        'gitlab.delete',
        {
          name: inst.name,
          ...(pin ? { pin } : {}),
          force_no_backup: forceNoBackup,
          remove_volumes: removeVolumes,
          force_orphan_runners: forceOrphanRunners,
        },
        { label: t('gitlab.instances.deleteLabel', { name: inst.name }) },
      ),
    onSuccess: (r) => {
      if (r?.locked) {
        setShowedLocked(true); // umožnit user zadat PIN
        return;
      }
      if (r?.ok === false) {
        // wrapper už zobrazil error toast — modal ponechat aby user upravil (např. force_no_backup)
        return;
      }
      toast.success(t('gitlab.instances.deletedOk', { name: inst.name }));
      onDeleted();
    },
  });

  const err = mut.data?.error;
  const backupFailed = mut.data?.backup_failed === true;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('gitlab.instances.deleteTitle', { name: inst.name })}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            loading={mut.isPending}
            disabled={showedLocked && !pin}
            onClick={() => mut.mutate()}
          >
            <Trash2 size={13} /> {t('gitlab.instances.deleteConfirm')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="text-sm text-zinc-300">
          <p>{t('gitlab.instances.deleteInfo')}</p>
          <ul className="mt-2 text-xs text-zinc-400 list-disc list-inside space-y-0.5">
            <li>{t('gitlab.instances.deleteStep1')}</li>
            <li>{t('gitlab.instances.deleteStep2')}</li>
            <li>{t('gitlab.instances.deleteStep3')}</li>
            <li>{t('gitlab.instances.deleteStep4')}</li>
          </ul>
        </div>

        {showedLocked && (
          <div className="rounded-md border border-yellow-700/40 bg-yellow-950/30 px-3 py-2">
            <p className="text-sm text-yellow-300 mb-2">
              🔒 {t('gitlab.instances.lockedInfo')}
            </p>
            <Input
              type="password"
              placeholder={t('gitlab.instances.pinPlaceholder')}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              autoFocus
            />
          </div>
        )}

        {backupFailed && (
          <div className="rounded-md border border-red-700/40 bg-red-950/30 px-3 py-2 text-sm text-red-300">
            {t('gitlab.instances.backupFailedHint')}
          </div>
        )}

        {err && !showedLocked && !backupFailed && (
          <div className="text-xs text-red-400 break-all">{err}</div>
        )}

        <div className="space-y-2 border-t border-zinc-800 pt-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={removeVolumes}
              onChange={(e) => setRemoveVolumes(e.target.checked)}
              className="accent-sky-500 mt-0.5"
            />
            <span>
              {t('gitlab.instances.removeVolumes')}
              <span className="block text-[11px] text-zinc-500">
                {t('gitlab.instances.removeVolumesHelp')}
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={forceNoBackup}
              onChange={(e) => setForceNoBackup(e.target.checked)}
              className="accent-red-500 mt-0.5"
            />
            <span>
              {t('gitlab.instances.forceNoBackup')}
              <span className="block text-[11px] text-zinc-500">
                {t('gitlab.instances.forceNoBackupHelp')}
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={forceOrphanRunners}
              onChange={(e) => setForceOrphanRunners(e.target.checked)}
              className="accent-red-500 mt-0.5"
            />
            <span>
              {t('gitlab.instances.forceOrphanRunners')}
              <span className="block text-[11px] text-zinc-500">
                {t('gitlab.instances.forceOrphanRunnersHelp')}
              </span>
            </span>
          </label>
        </div>
      </div>
    </Modal>
  );
}

// ── Bootstrap modal (dokončit provisioning + optional publish) ──────────────

function BootstrapModal({ inst, onClose, onDone }: {
  inst: Instance;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [domain, setDomain] = useState('');
  const [connectionId, setConnectionId] = useState(inst.name);

  const mut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok?: boolean; error?: string; host?: string; connection_id?: string }>(
        'gitlab.bootstrap',
        {
          name: inst.name,
          ...(domain ? { domain } : {}),
          ...(connectionId ? { connection_id: connectionId } : {}),
        },
        { label: t('gitlab.instances.bootstrapLabel', { name: inst.name }) },
      ),
    onSuccess: (r) => {
      if (r?.ok === false) return;
      toast.success(t('gitlab.instances.bootstrappedOk', { host: r?.host ?? '' }));
      onDone();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t('gitlab.instances.bootstrapTitle', { name: inst.name })}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={mut.isPending}
            onClick={() => mut.mutate()}
          >
            <Upload size={13} /> {t('gitlab.instances.bootstrapConfirm')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-zinc-300">
          {t('gitlab.instances.bootstrapInfo')}
        </p>
        <div>
          <label className="text-xs text-zinc-400 block mb-1">
            {t('gitlab.instances.bootstrapConnectionId')}
          </label>
          <Input value={connectionId} onChange={(e) => setConnectionId(e.target.value)} />
          <p className="text-[10px] text-zinc-500 mt-0.5">
            {t('gitlab.instances.bootstrapConnectionIdHelp')}
          </p>
        </div>
        <div>
          <label className="text-xs text-zinc-400 block mb-1">
            {t('gitlab.instances.bootstrapDomainOptional')}
          </label>
          <Input
            placeholder="gitlab.example.cz"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
          <p className="text-[10px] text-zinc-500 mt-0.5">
            {t('gitlab.instances.bootstrapDomainHelp')}
          </p>
        </div>
      </div>
    </Modal>
  );
}

// ── Upgrade modal (ujištění + spuštění) ─────────────────────────────────────
//
// GitLab sám v UI upozorňuje, že je potřeba novější verze. Upgrade mění běžící
// produkční instanci (recreate kontejneru + reconfigure), takže tool vyžaduje
// `confirm` — tady je to potvrzovací krok, ne skryté tlačítko.

function UpgradeModal(
  { inst, verdict, versions, versionsUnavailable, onClose, onDone }: {
    inst: Instance;
    verdict?: VersionVerdict;
    versions: string[];
    versionsUnavailable: boolean;
    onClose: () => void;
    onDone: () => void;
  },
) {
  const { t } = useTranslation();
  // Předvyplněný cíl = doporučení ze verdiktu (patch má přednost před novou
  // řadou). Bez něj dialog nešel odklikat — tlačítko čekalo na tag, který
  // uživatel neměl odkud vzít.
  const [version, setVersion] = useState(verdict?.recommended ?? '');
  const [understood, setUnderstood] = useState(false);

  const choices = (verdict?.newer_versions?.length ? verdict.newer_versions : versions)
    .filter((v) => v !== inst.version);

  const mut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok?: boolean; error?: string; version?: string }>(
        'gitlab.upgrade',
        { name: inst.name, version: version.trim(), confirm: true },
        { label: t('gitlab.instances.upgradeLabel', { name: inst.name }) },
      ),
    onSuccess: (r) => {
      if (r?.ok === false) return;
      toast.success(t('gitlab.instances.upgradeOk', { version: r?.version ?? version.trim() }));
      onDone();
    },
  });

  const canRun = understood && version.trim().length > 0 && !mut.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('gitlab.instances.upgradeTitle', { name: inst.name })}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant={verdict?.asap ? 'danger' : 'primary'}
            loading={mut.isPending}
            disabled={!canRun}
            onClick={() => mut.mutate()}
          >
            <ArrowUpCircle size={13} /> {t('gitlab.instances.upgradeConfirm')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {verdict?.asap && (
          <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-200">
            {t('gitlab.instances.upgradeAsapWarn', {
              version: verdict.recommended ?? verdict.latest_patch ?? '',
            })}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded border border-zinc-800 px-2 py-1.5">
            <div className="text-zinc-500">{t('gitlab.instances.upgradeRunning')}</div>
            <div className="font-mono text-zinc-200">{inst.version || '—'}</div>
          </div>
          <div className="rounded border border-zinc-800 px-2 py-1.5">
            <div className="text-zinc-500">{t('gitlab.instances.upgradeLatest')}</div>
            <div className="font-mono text-zinc-200">
              {verdict?.latest_version ?? '—'}
              {verdict && verdict.behind_by > 0 && (
                <span className="ml-1 text-[11px] text-zinc-500">
                  {t('gitlab.instances.upgradeBehind', { count: verdict.behind_by })}
                </span>
              )}
            </div>
          </div>
        </div>

        <p className="text-sm text-zinc-300">{t('gitlab.instances.upgradeInfo')}</p>

        <div>
          <label className="text-xs text-zinc-400 block mb-1">
            {t('gitlab.instances.upgradeVersion')}
          </label>
          {choices.length > 0
            ? (
              <select
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-sm text-zinc-100"
              >
                <option value="">{t('gitlab.instances.upgradePick')}</option>
                {choices.map((v) => (
                  <option key={v} value={v}>
                    {v}
                    {v === verdict?.latest_patch ? ` — ${t('gitlab.instances.versionAsap')}` : ''}
                    {v === verdict?.latest_version && v !== verdict?.latest_patch
                      ? ` — ${t('gitlab.instances.upgradeLatest')}`
                      : ''}
                  </option>
                ))}
              </select>
            )
            : (
              <Input
                placeholder="19.3.0-ce.0"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              />
            )}
          <p className="text-[10px] text-zinc-500 mt-0.5">
            {versionsUnavailable
              ? t('gitlab.instances.upgradeVersionsUnavailable')
              : t('gitlab.instances.upgradeVersionHelp')}
          </p>
        </div>

        <label className="flex items-start gap-2 text-xs text-zinc-300">
          <input
            type="checkbox"
            checked={understood}
            onChange={(e) => setUnderstood(e.target.checked)}
            className="accent-blue-500 mt-0.5"
          />
          <span>
            {t('gitlab.instances.upgradeAck')}
            <span className="block text-[11px] text-zinc-500">
              {t('gitlab.instances.upgradeAckHelp')}
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}

// ── Nastavení instance (co v GitLabu ovládá ACM) ────────────────────────────
//
// Filip 2026-08-24: „základní věci aby ovládala v tom gitlabu přímo platforma
// ACM a byly na to nástroje plus volby v UI." Čte gitlab.settings_get, ukládá
// přes gitlab.settings_apply — tedy tytéž tooly, které jedou po nasazení samy.

interface SettingsResp {
  ok: boolean;
  error?: string;
  host?: string;
  signup_enabled?: boolean;
  sso_only?: boolean;
  edition_title?: string | null;
  logo?: string | null;
  maintenance?: { keep_backups: number; prune_images: boolean; keep_images: number };
}

function SettingsModal({ inst, onClose }: { inst: Instance; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<{
    signup: boolean;
    ssoOnly: boolean;
    title: string;
    keepBackups: number;
    pruneImages: boolean;
  } | null>(null);

  const q = useQuery<SettingsResp>({
    queryKey: ['gitlab-settings', inst.name],
    queryFn: async () => {
      const r = await api.mcp<SettingsResp>('gitlab.settings_get', {}, { silent: true });
      if (r?.ok) {
        setForm({
          signup: r.signup_enabled === true,
          ssoOnly: r.sso_only === true,
          title: r.edition_title ?? '',
          keepBackups: r.maintenance?.keep_backups ?? 5,
          pruneImages: r.maintenance?.prune_images !== false,
        });
      }
      return r;
    },
  });

  const save = useMutation({
    mutationFn: () =>
      api.mcp<{ ok?: boolean; error?: string }>('gitlab.settings_apply', {
        signup_enabled: form?.signup ?? false,
        sso_only: form?.ssoOnly ?? true,
        ...(form?.title ? { edition_title: form.title } : {}),
        logo: false,
        maintenance: {
          keep_backups: form?.keepBackups ?? 5,
          prune_images: form?.pruneImages ?? true,
        },
      }, { label: t('gitlab.settings.saveLabel', { name: inst.name }) }),
    onSuccess: (r) => {
      if (r?.ok === false) return;
      toast.success(t('gitlab.settings.saved'));
      qc.invalidateQueries({ queryKey: ['gitlab-settings', inst.name] });
    },
  });

  const cleanNow = useMutation({
    mutationFn: () =>
      api.mcp<{ ok?: boolean; images?: { removed?: string[] } }>('gitlab.maintenance', {}, {
        label: t('gitlab.settings.cleanLabel'),
      }),
    onSuccess: (r) => {
      if (r?.ok === false) return;
      toast.success(t('gitlab.settings.cleanOk', { count: r?.images?.removed?.length ?? 0 }));
    },
  });

  const row = 'flex items-start justify-between gap-3 py-2 border-b border-zinc-900 last:border-0';

  return (
    <Modal
      open
      onClose={onClose}
      title={t('gitlab.settings.title', { name: inst.name })}
      footer={
        <div className="flex items-center justify-between gap-2 w-full">
          <Button
            variant="ghost"
            size="sm"
            loading={cleanNow.isPending}
            onClick={() => cleanNow.mutate()}
          >
            <Archive size={13} /> {t('gitlab.settings.cleanNow')}
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={!form}
              onClick={() => save.mutate()}
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
      }
    >
      {q.isLoading || !form
        ? <div className="text-sm text-zinc-500">{t('common.loading')}</div>
        : q.data?.ok === false
        ? <div className="text-sm text-red-300">{q.data.error}</div>
        : (
          <div className="text-sm">
            <div className={row}>
              <div>
                <div className="text-zinc-200">{t('gitlab.settings.signup')}</div>
                <div className="text-[11px] text-zinc-500">{t('gitlab.settings.signupHelp')}</div>
              </div>
              <input
                type="checkbox"
                className="accent-blue-500 mt-1"
                checked={form.signup}
                onChange={(e) => setForm({ ...form, signup: e.target.checked })}
              />
            </div>
            <div className={row}>
              <div>
                <div className="text-zinc-200">{t('gitlab.settings.ssoOnly')}</div>
                <div className="text-[11px] text-zinc-500">{t('gitlab.settings.ssoOnlyHelp')}</div>
              </div>
              <input
                type="checkbox"
                className="accent-blue-500 mt-1"
                checked={form.ssoOnly}
                onChange={(e) => setForm({ ...form, ssoOnly: e.target.checked })}
              />
            </div>
            <div className={row}>
              <div className="flex-1">
                <div className="text-zinc-200">{t('gitlab.settings.editionTitle')}</div>
                <Input
                  className="mt-1"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="GitLab ACM Edition"
                />
              </div>
            </div>
            <div className={row}>
              <div>
                <div className="text-zinc-200">{t('gitlab.settings.keepBackups')}</div>
                <div className="text-[11px] text-zinc-500">
                  {t('gitlab.settings.keepBackupsHelp')}
                </div>
              </div>
              <input
                type="number"
                min={0}
                max={50}
                className="w-16 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-right"
                value={form.keepBackups}
                onChange={(e) => setForm({ ...form, keepBackups: Number(e.target.value) })}
              />
            </div>
            <div className={row}>
              <div>
                <div className="text-zinc-200">{t('gitlab.settings.pruneImages')}</div>
                <div className="text-[11px] text-zinc-500">
                  {t('gitlab.settings.pruneImagesHelp')}
                </div>
              </div>
              <input
                type="checkbox"
                className="accent-blue-500 mt-1"
                checked={form.pruneImages}
                onChange={(e) => setForm({ ...form, pruneImages: e.target.checked })}
              />
            </div>
          </div>
        )}
    </Modal>
  );
}
